import React, { useEffect, useState } from 'react';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import { Form, Button, Col, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';
import cx from 'classnames';
import messages from '../../../locale/messages';
import { addBanner, updateBanner, getBannerPlacements, getAdminBannerShops, getAdminLocations } from '../../../actions/siteadmin/Banner';
import history from '../../../history';
import { api, bannerUploadDir } from '../../../config';
import s from '../PromoCode/PromoCodeForm/PromoCodeForm.css';
import ImageDropzone from './ImageDropzone';
import defaultIcon from '../../../../public/Icons/defaultIconAdmin.svg';
import withStyles from 'isomorphic-style-loader/withStyles';
import Switch from 'react-switch';
import Select from 'react-select';
import { CONTENT_LANGUAGES, DEFAULT_LANGUAGE } from '../../../helpers/contentLanguage';

// Site primary red, kept in sync with --btn-primary-bg in components/variables.css.
const PRIMARY_COLOR = 'var(--btn-primary-bg, #E62529)';

const renderField = ({ input, label, type, meta: { touched, error }, placeholder, required }) => (
  <Form.Group>
    <Form.Label>{label}{required && <span style={{ color: 'red' }}> *</span>}</Form.Label>
    <Form.Control {...input} type={type} placeholder={placeholder} isInvalid={touched && error} />
    {touched && error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
  </Form.Group>
);

const renderTextArea = ({ input, label, meta: { touched, error }, placeholder, required }) => (
  <Form.Group>
    <Form.Label>{label}{required && <span style={{ color: 'red' }}> *</span>}</Form.Label>
    <Form.Control as="textarea" rows={3} {...input} placeholder={placeholder} isInvalid={touched && error} />
    {touched && error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
  </Form.Group>
);

const renderSelect = ({ input, label, meta: { touched, error }, children, required }) => (
  <Form.Group>
    <Form.Label>{label}{required && <span style={{ color: 'red' }}> *</span>}</Form.Label>
    <Form.Control as="select" {...input} isInvalid={touched && error}>
      {children}
    </Form.Control>
    {touched && error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
  </Form.Group>
);

const renderDateTimeField = ({ input, label, meta: { touched, error }, placeholder, required }) => {
  const formatDateTimeForInput = (value) => {
    if (!value) return '';
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) {
      return value;
    }
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (e) {
      console.error('Error formatting date:', e, 'Value:', value);
      return '';
    }
  };

  const displayValue = formatDateTimeForInput(input.value);

  return (
    <Form.Group>
      <Form.Label>{label}{required && <span style={{ color: 'red' }}> *</span>}</Form.Label>
      <Form.Control
        {...input}
        type="datetime-local"
        placeholder={placeholder}
        isInvalid={touched && error}
        value={displayValue}
      />
      {touched && error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
    </Form.Group>
  );
};

const renderSwitch = ({ input, label }) => (
  <Form.Group style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
    <Form.Label style={{ margin: 0, fontWeight: 600 }}>{label}</Form.Label>
    <Switch
      checked={!!input.value}
      onChange={(checked) => input.onChange(checked)}
      onColor="#007bff"
      offColor="#ccc"
      checkedIcon={false}
      uncheckedIcon={false}
      height={22}
      width={44}
      handleDiameter={18}
    />
  </Form.Group>
);

const RenderShopSelect = ({ input, meta: { touched, error }, shopsOptions, searchLoading, handleShopSearchChange, initialAd }) => {
  let selectedValue = shopsOptions.find(opt => String(opt.value) === String(input.value)) || null;
  if (!selectedValue && input.value && initialAd && String(initialAd.shopId) === String(input.value)) {
    selectedValue = {
      value: initialAd.shopId,
      label: initialAd.shopName ? `${initialAd.shopName}` : 'Unnamed Restaurant'
    };
  }
  return (
    <Form.Group>
      <Form.Label>Select Linked Restaurant <span style={{ color: 'red' }}>*</span></Form.Label>
      <Select
        options={shopsOptions}
        value={selectedValue}
        onChange={(option) => input.onChange(option ? option.value : null)}
        onInputChange={handleShopSearchChange}
        placeholder="Type shop name to search..."
        isLoading={searchLoading}
        isClearable
        styles={{
          control: (base) => ({
            ...base,
            borderRadius: 8,
            minHeight: 42,
            borderColor: touched && error ? '#dc3545' : '#ccc',
            boxShadow: 'none',
            '&:hover': {
              borderColor: touched && error ? '#dc3545' : '#2684FF'
            }
          })
        }}
      />
      {touched && error && <div style={{ color: '#dc3545', fontSize: '80%', marginTop: '0.25rem' }}>{error}</div>}
    </Form.Group>
  );
};


const LanguageTabs = ({ languages, activeLanguage, onSelect, hasCopy, defaultLabel }) => (
  <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e5e5e5', marginBottom: 16 }}>
    {languages.map(language => {
      const isActive = language.code === activeLanguage;
      return (
        <button
          key={language.code}
          type="button"
          onClick={() => onSelect(language.code)}
          style={{
            border: 'none',
            background: 'none',
            padding: '8px 14px',
            marginBottom: -1,
            fontWeight: isActive ? 700 : 500,
            color: isActive ? PRIMARY_COLOR : '#666',
            borderBottom: `2px solid ${isActive ? PRIMARY_COLOR : 'transparent'}`,
            cursor: 'pointer'
          }}
        >
          {language.label}
          {language.code === DEFAULT_LANGUAGE && ` (${defaultLabel})`}
          {hasCopy(language.code) && (
            <span
              title="Has content"
              style={{
                display: 'inline-block',
                width: 6,
                height: 6,
                marginLeft: 6,
                borderRadius: '50%',
                background: '#28a745',
                verticalAlign: 'middle'
              }}
            />
          )}
        </button>
      );
    })}
  </div>
);

function joinUrl(...parts) {
  return parts
    .map((part, i) => {
      if (i === 0) return part.replace(/\/+$/, '');
      return part.replace(/^\/+/, '');
    })
    .join('/');
}

const ManageBannerForm = ({ handleSubmit, submitting, pristine, reset, intl, initialValues, mode, addBanner, updateBanner, placements = [], getBannerPlacements, getAdminBannerShops, getAdminLocations, imageUrl, placementId, isAd, tittle, translations, change }) => {
  const [shopsOptions, setShopsOptions] = useState([]);
  const [activeLanguage, setActiveLanguage] = useState(DEFAULT_LANGUAGE);
  const [searchLoading, setSearchLoading] = useState(false);
  const [locationOptions, setLocationOptions] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);

  useEffect(() => {
    getBannerPlacements && getBannerPlacements();
  }, [getBannerPlacements]);

  useEffect(() => {
    async function loadLocations() {
      if (getAdminLocations) {
        const locs = await getAdminLocations();
        setLocationOptions(locs.filter(l => l.isActive).map(l => ({ value: l.id, label: l.locationName })));
      }
    }
    loadLocations();
  }, [getAdminLocations]);

  useEffect(() => {
    if (initialValues && initialValues.locations && initialValues.locations.length > 0) {
      setSelectedLocations(initialValues.locations.map(l => ({ value: l.id, label: l.locationName })));
    }
  }, []);

  const initialShopId = initialValues?.ad?.shopId;
  const initialShopName = initialValues?.ad?.shopName;

  useEffect(() => {
    async function loadInitialShops() {
      if (getAdminBannerShops) {
        setSearchLoading(true);
        const shops = await getAdminBannerShops('');
        let options = shops.map(s => ({ value: s.id, label: `${s.shopName} (${s.email})` }));
        if (initialShopId) {
          const hasInitial = options.some(opt => String(opt.value) === String(initialShopId));
          if (!hasInitial) {
            options.unshift({
              value: initialShopId,
              label: initialShopName ? `${initialShopName}` : 'Unnamed Restaurant'
            });
          }
        }
        setShopsOptions(options);
        setSearchLoading(false);
      }
    }
    loadInitialShops();
  }, [getAdminBannerShops, initialShopId, initialShopName]);

  const handleShopSearch = async (inputValue) => {
    if (getAdminBannerShops) {
      setSearchLoading(true);
      const shops = await getAdminBannerShops(inputValue);
      const options = shops.map(s => ({ value: s.id, label: `${s.shopName} (${s.email})` }));
      setShopsOptions(options);
      setSearchLoading(false);
    }
  };

  const handleShopSearchChange = (inputValue, { action }) => {
    if (action === 'input-change') {
      handleShopSearch(inputValue);
    }
  };

  const selectedPlacement = placements.find(p => String(p.id) === String(placementId));
  const placementHeight = selectedPlacement ? selectedPlacement.height : undefined;
  const placementWidth = selectedPlacement ? selectedPlacement.width : undefined;

  // Dot on the tab: the default language reads the banner's own fields, every
  // other one reads its nested translation.
  const hasLanguageCopy = (code) => {
    if (code === DEFAULT_LANGUAGE) return !!tittle;
    const copy = translations && translations[code];
    return !!(copy && (copy.tittle || copy.description || copy.imageUrl));
  };

  const onSubmit = async (values) => {
    // Always send every non-default language, including the blank ones: the
    // mutation drops empty entries, which is how a translation gets cleared.
    const translationList = CONTENT_LANGUAGES
      .filter(language => language.code !== DEFAULT_LANGUAGE)
      .map(language => {
        const copy = (values.translations && values.translations[language.code]) || {};
        return {
          language: language.code,
          tittle: copy.tittle || null,
          description: copy.description || null,
          imageUrl: copy.imageUrl || null
        };
      });

    const formattedValues = {
      ...values,
      isAd: !!values.isAd,
      shopId: values.isAd ? values.shopId : null,
      startDate: values.startDate ? new Date(values.startDate).toISOString() : null,
      endDate: values.endDate ? new Date(values.endDate).toISOString() : null,
      locationIds: selectedLocations.map(l => l.value),
      translations: translationList
    };
    if (mode === 'edit') {
      await updateBanner(formattedValues);
    } else {
      await addBanner(formattedValues);
    }
    window.location.href = '/siteadmin/banner';
  };

  let previewUrl = defaultIcon;
  if (imageUrl) {
    previewUrl = joinUrl(api.apiEndpoint, bannerUploadDir, imageUrl);
  } else if (initialValues && initialValues.imageUrl) {
    previewUrl = joinUrl(api.apiEndpoint, bannerUploadDir, initialValues.imageUrl);
  }

  const languageImageUrl = (code) => (translations && translations[code] && translations[code].imageUrl) || '';

  // No override means the app falls back to the default image, so preview that
  // one rather than the placeholder icon.
  const languagePreviewUrl = (code) => {
    const value = languageImageUrl(code);
    return value ? joinUrl(api.apiEndpoint, bannerUploadDir, value) : previewUrl;
  };

  // The image stays parked in the right column but belongs to whichever
  // language tab is open, so the label has to name that language out loud.
  const activeIsDefault = activeLanguage === DEFAULT_LANGUAGE;
  const activePrefix = activeIsDefault ? '' : `translations.${activeLanguage}.`;
  const activeLanguageLabel = (CONTENT_LANGUAGES.find(entry => entry.code === activeLanguage) || {}).label || activeLanguage;

  const renderImageField = () => (
    <Form.Group className={s.space3}>
      <div className={cx(s.centerFlex, s.profileImgSection)}>
        <label>
          {intl.formatMessage(messages.imageUrl)}
          {' — '}
          <span style={{ color: PRIMARY_COLOR, fontWeight: 700 }}>{activeLanguageLabel}</span>
          {activeIsDefault && <span style={{ color: 'red' }}> *</span>}
        </label>
        <div
          className={s.backgroundImg}
          style={{ backgroundImage: `url(${activeIsDefault ? previewUrl : languagePreviewUrl(activeLanguage)})` }}
        />
        <div>
          {placementHeight && placementWidth ? (
            <Field
              // Remount per language so each dropzone gets its own hidden input.
              key={activeLanguage}
              name={`${activePrefix}imageUrl`}
              component={ImageDropzone}
              className={cx(s.btnSecondary, s.profileNoPadding)}
              subText={`Image Ratio Must ${placementWidth} : ${placementHeight}`}
              defaultMessage={intl.formatMessage(messages.chooseFile)}
              placementHeight={placementHeight}
              placementWidth={placementWidth}
              maxUploadSize={15}
            />
          ) : (
            <div style={{ color: 'red' }}>
              Please select Placement first to upload an image.
            </div>
          )}
        </div>
      </div>
      {!activeIsDefault && (
        <p style={{ fontSize: 12, color: '#888', marginTop: 8, marginBottom: 0 }}>
          {intl.formatMessage(messages.translationFallbackHint)}
        </p>
      )}
      {!activeIsDefault && languageImageUrl(activeLanguage) && (
        <Button
          type="button"
          variant="link"
          style={{ padding: 0, fontSize: 12 }}
          onClick={() => change(`${activePrefix}imageUrl`, '')}
        >
          {intl.formatMessage(messages.remove)}
        </Button>
      )}
    </Form.Group>
  );

  return (
    <Form
      onSubmit={handleSubmit(onSubmit)}
      style={{
        padding: "20px",
        background: "#fff",
        borderRadius: 8,
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
      }}
    >
      <Button
        variant="outline-secondary"
        type="button"
        onClick={() =>
          history && history.push && history.push("/siteadmin/banner")
        }
        style={{ marginBottom: 16, fontWeight: 600 }}
      >
        &#8592; Go Back
      </Button>
      <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 24 }}>
        {mode === "edit"
          ? intl.formatMessage(messages.update)
          : intl.formatMessage(messages.addBanner)}
      </h2>
      <Row>
        <Col md={6}>
          <LanguageTabs
            languages={CONTENT_LANGUAGES}
            activeLanguage={activeLanguage}
            onSelect={setActiveLanguage}
            hasCopy={hasLanguageCopy}
            defaultLabel={intl.formatMessage(messages.languageDefault)}
          />
          {CONTENT_LANGUAGES.map(language => {
            const isDefault = language.code === DEFAULT_LANGUAGE;
            // Default copy lives on the banner row itself; the rest is nested
            // under translations.<code> and picked apart again in onSubmit.
            const prefix = isDefault ? '' : `translations.${language.code}.`;
            return (
              // Kept mounted so switching tabs never drops what was typed.
              <div key={language.code} style={{ display: activeLanguage === language.code ? 'block' : 'none' }}>
                <Field
                  name={`${prefix}tittle`}
                  type="text"
                  component={renderField}
                  label={intl.formatMessage(messages.tittle)}
                  placeholder={intl.formatMessage(messages.tittle)}
                  required={isDefault}
                />
                <Field
                  name={`${prefix}description`}
                  component={renderTextArea}
                  label={intl.formatMessage(messages.description)}
                  placeholder={intl.formatMessage(messages.description)}
                />
                {/* The dropzone itself lives in the right column and follows the
                    active tab; this keeps the value registered either way. */}
                {!isDefault && <Field name={`${prefix}imageUrl`} component="input" type="hidden" />}
              </div>
            );
          })}
          {activeLanguage !== DEFAULT_LANGUAGE && (
            <p style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
              {intl.formatMessage(messages.translationFallbackHint)}
            </p>
          )}

          <hr style={{ margin: '24px 0', borderColor: '#eee' }} />

          <Field name="imageUrl" component="input" type="hidden" />
          <Field
            name="status"
            component={renderSelect}
            label={intl.formatMessage(messages.status)}
          >
            <option value="DRAFT">Draft</option>
            <option value="PUBLISHED">Published</option>
            <option value="ARCHIVED">Archived</option>
          </Field>
          <Field
            name="placementId"
            component={renderSelect}
            label={intl.formatMessage(messages.placement)}
            required={true}
            normalize={v => v ? parseInt(v, 10) : v}
          >
            <option value="">{intl.formatMessage(messages.placement)}</option>
            {placements &&
              placements.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </Field>

          <Field
            name="intent"
            type="text"
            component={renderField}
            label={intl.formatMessage(messages.intent)}
            placeholder={intl.formatMessage(messages.intent)}
          />

          <hr style={{ margin: '24px 0', borderColor: '#eee' }} />

          <h4 style={{ fontSize: 16, fontWeight: 600, color: '#333', marginBottom: 4 }}>Location Targeting</h4>
          <p style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
            Leave empty to show globally. Select locations to restrict visibility by geofence.
          </p>
          <Select
            isMulti
            options={locationOptions}
            value={selectedLocations}
            onChange={setSelectedLocations}
            placeholder="Select locations"
            closeMenuOnSelect={false}
            noOptionsMessage={() => 'No active zones found'}
            styles={{
              control: (base) => ({
                ...base,
                borderRadius: 8,
                minHeight: 42,
                borderColor: '#ccc',
                boxShadow: 'none',
                '&:hover': { borderColor: '#2684FF' }
              })
            }}
          />

          <hr style={{ margin: '24px 0', borderColor: '#eee' }} />

          <h4 style={{ fontSize: 16, fontWeight: 600, color: '#333', marginBottom: 12 }}>Advertisement Settings</h4>
          <Field
            name="isAd"
            component={renderSwitch}
            label="Enable as Advertisement"
          />
          {isAd && (
            <Field
              name="shopId"
              component={RenderShopSelect}
              shopsOptions={shopsOptions}
              searchLoading={searchLoading}
              handleShopSearchChange={handleShopSearchChange}
              initialAd={initialValues && initialValues.ad}
            />
          )}
        </Col>
        <Col md={6}>
          <Field
            name="startDate"
            component={renderDateTimeField}
            label={intl.formatMessage(messages.startDate)}
            placeholder={intl.formatMessage(messages.startDate)}
            required={true}
          />
          <Field
            name="endDate"
            component={renderDateTimeField}
            label={intl.formatMessage(messages.endDate)}
            placeholder={intl.formatMessage(messages.endDate)}
          />
          <Field
            name="priority"
            type="number"
            component={renderField}
            label={intl.formatMessage(messages.priority)}
            placeholder={intl.formatMessage(messages.priority)}
          />
          {renderImageField()}
        </Col>
      </Row>
      <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
        <Button
          type="submit"
          disabled={submitting || (mode !== 'edit' && pristine)}
          variant="primary"
        >
          {mode === "edit"
            ? intl.formatMessage(messages.update)
            : intl.formatMessage(messages.addBanner)}
        </Button>
        <Button
          type="button"
          disabled={submitting || pristine}
          onClick={reset}
          variant="secondary"
        >
          <FormattedMessage {...messages.reset} />
        </Button>
      </div>
    </Form>
  );
};

const validate = values => {
  const errors = {};
  if (!values.tittle) {
    errors.tittle = 'Title is required';
  }
  if (!values.placementId) {
    errors.placementId = 'Placement is required';
  }
  if (!values.startDate) {
    errors.startDate = 'Start date is required';
  }
  if (values.isAd && !values.shopId) {
    errors.shopId = 'Shop selection is required for advertisements';
  }
  return errors;
};

const selector = formValueSelector('ManageBannerForm');
const mapState = state => ({
  placements: state.adminBanners && state.adminBanners.placements ? state.adminBanners.placements : [],
  imageUrl: selector(state, 'imageUrl'),
  placementId: selector(state, 'placementId'),
  isAd: selector(state, 'isAd'),
  tittle: selector(state, 'tittle'),
  translations: selector(state, 'translations')
});

const mapDispatch = {
  addBanner,
  updateBanner,
  getBannerPlacements,
  getAdminBannerShops,
  getAdminLocations
};

export default withStyles(s)(
  connect(mapState, mapDispatch)(
    injectIntl(
      reduxForm({ form: 'ManageBannerForm', validate, enableReinitialize: true })(ManageBannerForm)
    )
  )
);